const TimelineEventBlock = ({
  event,
  orientation,
  positionStyle,
  onClick,
  showTimeLabels = true,
}: TimelineEventBlockProps) => {
  const baseClasses =
    "absolute flex flex-col justify-center px-3 py-2 text-xs text-primary-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400";

  const orientationClasses =
    orientation === "horizontal"
      ? "top-2 bottom-2 rounded-xl border-2 border-primary-400 bg-primary-400/10 hover:border-primary-300 hover:bg-primary-400/20 dark:border-primary-300/30 dark:bg-primary-400/20 dark:text-primary-100"
      : "left-3 right-3 rounded-md border-2 border-primary-400/40 bg-primary-500/15 shadow-sm hover:border-primary-300 hover:bg-primary-500/25 dark:border-primary-300/30 dark:bg-primary-400/20 dark:text-primary-100";

  const interactiveProps =
    typeof onClick === "function"
      ? {
          role: "button" as const,
          tabIndex: 0,
          onClick,
        }
      : {};

  // For vertical orientation, hide time on mobile, show on desktop
  const timeClasses =
    orientation === "vertical"
      ? "mt-1 text-left text-[11px] text-primary-500 dark:text-primary-200 hidden md:block"
      : "mt-1 text-left text-[11px] text-primary-500 dark:text-primary-200";

  return (
    <div
      className={`${baseClasses} ${orientationClasses}`}
      style={positionStyle}
      {...interactiveProps}>
      <span className="truncate text-left font-semibold text-primary-500 overflow-visible whitespace-normal break-all text-overflow:clip">
        {event.label}
      </span>
      {showTimeLabels && (
        <span className={timeClasses}>
          {formatUtcWallTimeLabel(event.start)} —{" "}
          {formatUtcWallTimeLabel(event.end)}
        </span>
      )}
    </div>
  );
};

import { MeetingRoom, MeetingRoomRent } from "@/api/meetingRooms";
import {
  formatUtcWallTimeLabel,
  timelineRangeUtcBounds,
} from "@/utils/meetingRoomWallClock";
import {
  addHours,
  addMinutes,
  differenceInMinutes,
  isAfter,
  isBefore,
} from "date-fns";
import {
  CSSProperties,
  Fragment,
  MouseEvent,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";

export interface TimelineEvent {
  id: number;
  start: Date;
  end: Date;
  label: string;
  rent?: MeetingRoomRent;
}

export interface TimelineRoom {
  room: MeetingRoom;
  events: TimelineEvent[];
  isLoading?: boolean;
  error?: string | null;
}

export interface MeetingRoomTimelineProps {
  date: Date;
  rooms: TimelineRoom[];
  orientation?: "horizontal" | "vertical";
  intervalMinutes?: number;
  startHour?: number;
  endHour?: number;
  onRoomClick?: (room: MeetingRoom) => void;
  onEventClick?: (room: MeetingRoom, event: TimelineEvent) => void;
  onEmptySlotClick?: (
    room: MeetingRoom,
    data: { start: Date; end: Date },
  ) => void;
}

function clampDateToRange(date: Date, rangeStart: Date, rangeEnd: Date) {
  if (isBefore(date, rangeStart)) return rangeStart;
  if (isAfter(date, rangeEnd)) return rangeEnd;
  return date;
}

interface TooltipState {
  offsetPercent: number;
  label: string;
}

const RoomInfoCard = ({
  room,
  onClick,
}: {
  room: MeetingRoom;
  onClick?: (room: MeetingRoom) => void;
}) => {
  const handleClick = useCallback(() => {
    onClick?.(room);
  }, [onClick, room]);

  return (
    <button
      type="button"
      onClick={handleClick}
      className="flex w-full flex-col gap-2 rounded-xl border-2 border-foreground/10 bg-content1/60 p-4 text-left transition-colors hover:border-primary-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 md:h-full">
      <span className="text-lg font-semibold">{room.display_name}</span>
      <span className="text-sm text-foreground-500">
        {room.places} мест · {room.floor} этаж
      </span>
      {room.features.length > 0 ? (
        <span className="text-xs uppercase text-foreground-400">
          Есть {room.features.join(", ")}
        </span>
      ) : null}
    </button>
  );
};

const TimelineErrorBanner = ({ message }: { message: string }) => (
  <div className="pointer-events-none absolute inset-x-2 bottom-2 rounded-md border border-danger-300 bg-danger-100/80 px-3 py-1 text-[11px] text-danger-600 dark:border-danger-400/50 dark:bg-danger-500/20 dark:text-danger-200">
    {message}
  </div>
);

const TimeAxis = ({
  rangeStart,
  rangeEnd,
  totalMinutes,
  effectiveHeightPx,
}: TimeAxisProps) => {
  const hourMarks = useMemo(() => {
    const marks: Date[] = [];
    let current = new Date(rangeStart);
    const endMs = rangeEnd.getTime();

    while (current.getTime() <= endMs) {
      marks.push(new Date(current));
      current = addHours(current, 1);
    }

    return marks;
  }, [rangeStart, rangeEnd]);

  return (
    <div
      className="relative flex-shrink-0 pr-2 block md:hidden"
      style={{ height: effectiveHeightPx }}>
      {hourMarks.map((mark) => {
        const offsetMinutes = differenceInMinutes(mark, rangeStart);
        const topPx = (offsetMinutes / totalMinutes) * effectiveHeightPx;

        return (
          <div
            key={mark.getTime()}
            className="absolute right-[-4px] flex items-center text-[11px] text-foreground-500"
            style={{ top: `${topPx}px`, transform: "translateY(-50%)" }}>
            {formatUtcWallTimeLabel(mark)}
          </div>
        );
      })}
    </div>
  );
};

function useHorizontalInteractionHelpers(
  totalMinutes: number,
  rangeStart: Date,
  rangeEnd: Date,
) {
  const resolveTimeFromRatio = useCallback(
    (ratio: number) => {
      const minutes = ratio * totalMinutes;
      const target = addMinutes(rangeStart, minutes);
      return clampDateToRange(target, rangeStart, rangeEnd);
    },
    [rangeEnd, rangeStart, totalMinutes],
  );

  return { resolveTimeFromRatio };
}

function useVerticalInteractionHelpers(
  totalMinutes: number,
  rangeStart: Date,
  rangeEnd: Date,
) {
  const resolveTimeFromRatio = useCallback(
    (ratio: number) => {
      const minutes = ratio * totalMinutes;
      const target = addMinutes(rangeStart, minutes);
      return clampDateToRange(target, rangeStart, rangeEnd);
    },
    [rangeEnd, rangeStart, totalMinutes],
  );

  return { resolveTimeFromRatio };
}

function snapTimeToInterval(
  date: Date,
  rangeStart: Date,
  intervalMinutes: number,
) {
  const diff = differenceInMinutes(date, rangeStart);
  const snapped = Math.floor(diff / intervalMinutes) * intervalMinutes;
  return addMinutes(rangeStart, snapped);
}

interface TimelineSharedProps {
  slots: Date[];
  totalMinutes: number;
  rangeStart: Date;
  rangeEnd: Date;
  intervalMinutes: number;
  onRoomClick?: (room: MeetingRoom) => void;
  onEventClick?: (room: MeetingRoom, event: TimelineEvent) => void;
  onEmptySlotClick?: (
    room: MeetingRoom,
    data: { start: Date; end: Date },
  ) => void;
}

interface TimelineRoomSectionProps extends TimelineSharedProps {
  room: MeetingRoom;
  events: TimelineEvent[];
  isLoading?: boolean;
  error?: string | null;
}

interface TimelineEventBlockProps {
  event: TimelineEvent;
  orientation: "horizontal" | "vertical";
  positionStyle: CSSProperties;
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
  showTimeLabels?: boolean;
}

interface TimeAxisProps {
  rangeStart: Date;
  rangeEnd: Date;
  totalMinutes: number;
  effectiveHeightPx: number;
}

const HorizontalTimelineRoom = ({
  room,
  events,
  isLoading,
  error,
  slots,
  totalMinutes,
  rangeStart,
  rangeEnd,
  intervalMinutes,
  onRoomClick,
  onEventClick,
  onEmptySlotClick,
}: TimelineRoomSectionProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const { resolveTimeFromRatio } = useHorizontalInteractionHelpers(
    totalMinutes,
    rangeStart,
    rangeEnd,
  );

  const calculateRatio = useCallback((event: MouseEvent<HTMLDivElement>) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const raw = (event.clientX - rect.left) / rect.width;
    if (Number.isNaN(raw)) return null;
    return Math.min(Math.max(raw, 0), 1);
  }, []);

  const handleMouseMove = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      const ratio = calculateRatio(event);
      if (ratio == null) {
        setTooltip(null);
        return;
      }
      const time = resolveTimeFromRatio(ratio);
      setTooltip({
        offsetPercent: ratio * 100,
        label: formatUtcWallTimeLabel(time),
      });
    },
    [calculateRatio, resolveTimeFromRatio],
  );

  const handleMouseLeave = useCallback(() => {
    setTooltip(null);
  }, []);

  const handleEmptyClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (!onEmptySlotClick || isLoading) return;
      const ratio = calculateRatio(event);
      if (ratio == null) return;
      const rawStart = resolveTimeFromRatio(ratio);
      const start = snapTimeToInterval(rawStart, rangeStart, intervalMinutes);
      const end = addMinutes(start, intervalMinutes);
      onEmptySlotClick(room, { start, end });
    },
    [
      calculateRatio,
      intervalMinutes,
      onEmptySlotClick,
      rangeStart,
      resolveTimeFromRatio,
      room,
    ],
  );

  return (
    <Fragment key={room.id}>
      <div className="grid grid-cols-1 gap-4 rounded-2xl md:grid-cols-[220px_1fr]">
        <RoomInfoCard room={room} onClick={onRoomClick} />
        <div
          ref={containerRef}
          className="relative min-h-48 overflow-hidden rounded-xl border border-foreground/10 bg-background"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          onClick={handleEmptyClick}>
          <div className="pointer-events-none absolute inset-0">
            {slots.slice(1, -1).map((slot, index) => {
              const offsetPercent = ((index + 1) / (slots.length - 1)) * 100;
              const isHour =
                differenceInMinutes(slot, rangeStart) % 60 === 0;
              return (
                <div
                  key={slot.getTime()}
                  className={`absolute top-2 bottom-2 ${
                    isHour
                      ? "border-l border-foreground-200/40"
                      : "border-l border-foreground/10"
                  }`}
                  style={{ left: `${offsetPercent}%` }}
                />
              );
            })}
          </div>

          {tooltip ? (
            <div
              className="pointer-events-none absolute top-2 -translate-x-1/2 rounded-md bg-default-900 px-2 py-1 text-[11px] font-medium text-default-50 shadow-md"
              style={{ left: `${tooltip.offsetPercent}%` }}>
              {tooltip.label}
            </div>
          ) : null}

          <div className="relative h-full">
            {isLoading ? (
              <div className="pointer-events-none absolute inset-2 rounded-lg skeleton-shimmer" />
            ) : null}
            {!isLoading && events.length === 0 ? (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-xs text-foreground-400">
                Свободно весь день
              </div>
            ) : null}
            {events.map((event) => {
              const clampedStart = clampDateToRange(
                event.start,
                rangeStart,
                rangeEnd,
              );
              const clampedEnd = clampDateToRange(
                event.end,
                rangeStart,
                rangeEnd,
              );

              if (!isAfter(clampedEnd, clampedStart)) return null;

              const offsetMinutes = differenceInMinutes(
                clampedStart,
                rangeStart,
              );
              const eventMinutes = Math.max(
                differenceInMinutes(clampedEnd, clampedStart),
                intervalMinutes / 2,
              );
              const offsetPercent = (offsetMinutes / totalMinutes) * 100;
              const widthPercent = (eventMinutes / totalMinutes) * 100;

              const handleEventClick = (
                clicked: MouseEvent<HTMLDivElement>,
              ) => {
                clicked.stopPropagation();
                onEventClick?.(room, event);
              };

              const positionStyle: CSSProperties = {
                left: `${offsetPercent}%`,
                width: `${Math.max(widthPercent, 4)}%`,
                minWidth: "3rem",
              };

              return (
                <TimelineEventBlock
                  key={event.id}
                  event={{ ...event, start: clampedStart, end: clampedEnd }}
                  orientation="horizontal"
                  positionStyle={positionStyle}
                  onClick={handleEventClick}
                />
              );
            })}
          </div>

          {error ? <TimelineErrorBanner message={error} /> : null}
        </div>
      </div>
      <div className="h-1 w-full rounded-full bg-foreground/10 last:hidden md:block" />
    </Fragment>
  );
};

const VerticalTimelineRoom = ({
  room,
  events,
  isLoading,
  error,
  slots,
  totalMinutes,
  rangeStart,
  rangeEnd,
  intervalMinutes,
  onRoomClick,
  onEventClick,
  onEmptySlotClick,
}: TimelineRoomSectionProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { resolveTimeFromRatio } = useVerticalInteractionHelpers(
    totalMinutes,
    rangeStart,
    rangeEnd,
  );
  const effectiveHeightPx = Math.max(totalMinutes * 0.6, 320);

  const calculateRatio = useCallback((clientY: number) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const raw = (clientY - rect.top) / rect.height;
    if (Number.isNaN(raw)) return null;
    return Math.min(Math.max(raw, 0), 1);
  }, []);

  const handleEmptyClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (!onEmptySlotClick || isLoading) return;
      const ratio = calculateRatio(event.clientY);
      if (ratio == null) return;
      const rawStart = resolveTimeFromRatio(ratio);
      const start = snapTimeToInterval(rawStart, rangeStart, intervalMinutes);
      const end = addMinutes(start, intervalMinutes);
      onEmptySlotClick(room, { start, end });
    },
    [
      calculateRatio,
      intervalMinutes,
      onEmptySlotClick,
      rangeStart,
      resolveTimeFromRatio,
      room,
    ],
  );

  return (
    <div
      key={room.id}
      className="flex flex-col gap-4 rounded-2xl border border-foreground/10 bg-content1/60 p-4 shadow-sm">
      <RoomInfoCard room={room} onClick={onRoomClick} />
      <div className="flex gap-2">
        <TimeAxis
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalMinutes={totalMinutes}
          effectiveHeightPx={effectiveHeightPx}
        />
        <div
          ref={containerRef}
          className="relative flex-1 border-l border-foreground/20 pl-4"
          style={{ minHeight: effectiveHeightPx, height: effectiveHeightPx }}
          onClick={handleEmptyClick}>
          <div className="pointer-events-none absolute inset-0">
            {slots.slice(1, -1).map((slot) => {
              const offsetMinutes = differenceInMinutes(slot, rangeStart);
              const top = (offsetMinutes / totalMinutes) * 100;
              const isHour =
                differenceInMinutes(slot, rangeStart) % 60 === 0;
              return (
                <div
                  key={slot.getTime()}
                  className={`absolute left-0 right-0 ${
                    isHour
                      ? "border-t border-foreground-200/40"
                      : "border-t border-foreground/10"
                  }`}
                  style={{ top: `${top}%` }}
                />
              );
            })}
          </div>
          <div className="relative">
            {isLoading ? (
              <div className="pointer-events-none absolute left-2 right-2 top-0 bottom-0 rounded-md skeleton-shimmer" />
            ) : null}
            {!isLoading && events.length === 0 ? (
              <div className="py-6 text-center text-xs text-foreground-400">
                Свободно весь день
              </div>
            ) : null}
            {events.map((event) => {
              const clampedStart = clampDateToRange(
                event.start,
                rangeStart,
                rangeEnd,
              );
              const clampedEnd = clampDateToRange(
                event.end,
                rangeStart,
                rangeEnd,
              );

              if (!isAfter(clampedEnd, clampedStart)) return null;

              const startMinutes = differenceInMinutes(
                clampedStart,
                rangeStart,
              );
              const durationMinutes = Math.max(
                differenceInMinutes(clampedEnd, clampedStart),
                intervalMinutes / 2,
              );

              const topOffsetPx =
                (startMinutes / totalMinutes) * effectiveHeightPx;
              const durationPx =
                (durationMinutes / totalMinutes) * effectiveHeightPx;

              const handleEventClick = (
                clicked: MouseEvent<HTMLDivElement>,
              ) => {
                clicked.stopPropagation();
                onEventClick?.(room, event);
              };

              const positionStyle: CSSProperties = {
                top: topOffsetPx,
                height: Math.max(durationPx, 36),
              };

              return (
                <TimelineEventBlock
                  key={event.id}
                  event={{ ...event, start: clampedStart, end: clampedEnd }}
                  orientation="vertical"
                  positionStyle={positionStyle}
                  onClick={handleEventClick}
                />
              );
            })}
          </div>
        </div>
      </div>
      {error ? <TimelineErrorBanner message={error} /> : null}
    </div>
  );
};

export function MeetingRoomTimeline({
  date,
  rooms,
  orientation = "horizontal",
  intervalMinutes = 30,
  startHour = 8,
  endHour = 22,
  onRoomClick,
  onEventClick,
  onEmptySlotClick,
}: MeetingRoomTimelineProps) {
  const { rangeStart, rangeEnd } = timelineRangeUtcBounds(
    date,
    startHour,
    endHour,
  );

  const totalMinutes = Math.max(
    differenceInMinutes(rangeEnd, rangeStart),
    intervalMinutes,
  );

  const rangeStartMs = rangeStart.getTime();
  const rangeEndMs = rangeEnd.getTime();

  const slots = useMemo(() => {
    const points: Date[] = [];
    let current = rangeStartMs;
    const step = intervalMinutes * 60 * 1000;

    while (current <= rangeEndMs) {
      points.push(new Date(current));
      current += step;
    }

    if (
      points.length === 0 ||
      points[points.length - 1].getTime() !== rangeEndMs
    ) {
      points.push(new Date(rangeEndMs));
    }

    return points;
  }, [intervalMinutes, rangeEndMs, rangeStartMs]);

  const renderHorizontalTimeline = () => (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-6">
        {rooms.map(({ room, events, isLoading, error }) => (
          <HorizontalTimelineRoom
            key={room.id}
            room={room}
            events={events}
            isLoading={isLoading}
            error={error}
            slots={slots}
            totalMinutes={totalMinutes}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            intervalMinutes={intervalMinutes}
            onRoomClick={onRoomClick}
            onEventClick={onEventClick}
            onEmptySlotClick={onEmptySlotClick}
          />
        ))}
      </div>
    </div>
  );

  const renderVerticalTimeline = () => (
    <div className="flex flex-col gap-6">
      {rooms.map(({ room, events, isLoading, error }) => (
        <VerticalTimelineRoom
          key={room.id}
          room={room}
          events={events}
          isLoading={isLoading}
          error={error}
          slots={slots}
          totalMinutes={totalMinutes}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          intervalMinutes={intervalMinutes}
          onRoomClick={onRoomClick}
          onEventClick={onEventClick}
          onEmptySlotClick={onEmptySlotClick}
        />
      ))}
    </div>
  );

  if (rooms.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-foreground/10 bg-content2/40 p-6 text-sm text-foreground-500">
        <span>Нет данных для отображения таймлайна.</span>
      </div>
    );
  }

  return orientation === "vertical"
    ? renderVerticalTimeline()
    : renderHorizontalTimeline();
}
